<template>
  <v-dialog
    ref="dialog"
    v-model="dialogOpened"
    transition="dialog-bottom-transition"
    fullscreen
    persistent
    no-click-animation
    hide-overlay
    :retain-focus="false"
    @keydown.esc="_back"
  >
    <v-card>
      <v-toolbar dark color="primary">
        <v-toolbar-title>{{title}}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="_back">
          <v-icon>fal fa-times</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="d-flex flex-column pt-4 px-4">
        <div>
          <div class="headline text-center">
            <v-icon color="secondary" class="mb-4">fad fa-stream</v-icon>
          </div>

          <v-alert v-if="error" type="error" text>{{error}}</v-alert>

          <v-alert v-if="limitReached" type="warning" text>
            Du hast dein Limit von {{limitReached.limit}} Räumen erreicht.<br>
            Upgrade deinen <a :href="accountUrl" target="_blank">Tarif</a>
            und erstelle weitere Räume.
          </v-alert>

          <div :disabled="!!limitReached">

            <v-text-field
              ref="name"
              v-model="formData.name"
              :rules="rules(REQUIRED)"
              :readonly="loading"
              minlength="2"
              maxlength="30"
              placeholder="Name des Raumes eingeben"
              label="Raumname"
              outlined
            ></v-text-field>

            <v-textarea
              ref="description"
              v-model="formData.description"
              :rules="rules()"
              :readonly="loading"
              maxlength="255"
              placeholder="Beschreibung des Raumes eingeben"
              label="Raumbeschreibung (optional)"
              rows="1"
              outlined
              auto-grow
            ></v-textarea>

            <div class="d-flex">
              <div class="flex mb-6">
                Privater Raum
                <div>
                  <small v-if="formData.private">
                    Nur du kannst weitere Benutzer einladen.
                  </small>
                  <small v-if="!formData.private">
                    Jeder beigetretene Benutzer kann weitere Benutzer einladen.
                  </small>
                </div>
              </div>
              <v-switch v-model="formData.private" color="primary" class="pt-0 mt-0"></v-switch>
            </div>

            <div v-if="room && room.participants && room.participants.length">
              {{room.participants.length}} Benutzer

              <div
                v-for="(participant, i) in room.participants"
                :key="i"
                class="d-flex align-center my-2">
                <div class="participant d-flex align-center justify-center flex-shrink-0">
                  {{_initials(participant.name)}}
                </div>
                <div class="flex text-truncate px-3">{{participant.name}}</div>
                <v-btn icon @click="_removeParticipant(participant.id, i)">
                  <v-icon small color="error">far fa-user-minus</v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-auto pt-3" :disabled="!!limitReached">
          <v-btn
            :loading="loading_create"
            :disabled="loading"
            class="text-center"
            color="accent"
            depressed
            rounded
            large
            block
            @click="_submit"
          >{{submit}}</v-btn>
          <slot></slot>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
  import {focusField, participantInitials, routeOptions, accountUrl} from '../../assets/js/utils';
  import Dialog from './Dialog';


  export default {
    name: 'RoomCreateDialog',

    extends: Dialog,

    props: {
      rooms: {
        type: Array,
        default: () => []
      }
    },

    data() {
      return {
        title: 'Neuen Raum erstellen',
        submit: 'Raum erstellen',
        operationId: 'RoomCreate',
        errorMessage: 'Raum konnte nicht erstellt werden.',
        formData: {
          name: '',
          description: '',
          private: true
        },
        room: null,
        validate: false,
        loading: false,
        loading_create: false,
        error: false,
        REQUIRED: true,
        accountUrl,
        // Dialog
        routePath: '/share/rooms/create'
      };
    },

    computed: {
      rules() {
        return (required) => {
          if (!this.validate) {
            return [() => true];
          }

          const rules = [
            (value) => !value || value.length >= 4 || 'Mind. 4 Zeichen',
            (value) => !value || value.length <= 30 || 'Max. 30 Zeichen'
          ];

          if (required) {
            rules.unshift((value) => !!value || 'Name erforderlich');
          }

          return rules;
        };
      },

      limitReached() {
        if (this.$options.name !== 'RoomCreateDialog') {
          return false;
        }

        const user = this.$store.getters['session/getUser'];
        const limit = user.limits?.roomCount ?? 0;
        const rooms_own = this.rooms.filter((room) => {
          return room.owner?.id === user.id;
        });

        if (limit > 0 && rooms_own.length >= limit) {
          return {limit};
        }
        return false;
      }
    },

    watch: {
      dialogOpened(opened) {
        if (!opened) {
          this.validate = false;
          this.error = false;
          return;
        }

        this.$nextTick(() => {
          if (this.limitReached) {
            return;
          }
          const input = focusField(this.$refs.name);
          input.setSelectionRange(0, input.value.length);
          input.setAttribute('autocomplete', 'off');
        });
      },

      loading(loading) {
        this.$emit('loading', loading);
      },

      loading_create(loading_create) {
        this.$emit('loading-create', loading_create);
      }
    },

    methods: {
      reset() {
        this.formData = {...this.$options.data().formData};
      },

      _back() {
        this.$router.replace(routeOptions({path: '/share/rooms'}));
      },

      _submit() {
        if (this.loading) {
          return;
        }

        this.error = false;
        this.validate = true;

        this.$nextTick(() => {
          if (this.$refs.name.valid) {
            this.__submit();
          }
        });
      },

      async __submit() {
        this.loading = true;
        this.loading_create = true;

        try {
          const formData = {...this.formData};

          // delete empty fields
          for (const key in formData) {
            if (formData[key] === '') {
              delete formData[key];
            }
          }

          const response = await this.$api.post(this.operationId, formData);
          if (this.$options.name === 'RoomCreateDialog') {
            // Connect to new room.
            const room = response.data;
            this.$router.push({path: '/share', query: {room: room.id}});

          } else if (this.$options.name === 'RoomUpdateDialog') {
            this._back();
          }

          // Revert values.
          this.$nextTick(() => this.reset());

        } catch (error) {
          this.error = this.errorMessage;
        }

        this.loading = false;
        this.loading_create = false;
      },

      _initials(participant) {
        return participantInitials(participant);
      },

      _removeParticipant(participantId, participantsIndex) {
        if (Array.isArray(this.formData.removeParticipants)) {
          this.formData.removeParticipants.push(participantId);
        } else {
          this.formData.removeParticipants = [participantId];
        }
        this.room.participants.splice(participantsIndex, 1);
      }
    }
  };
</script>

<style scoped>
  .headline .v-icon {
    font-size: 50px;
  }

  [disabled] {
    opacity: 0.5;
    pointer-events: none;
  }
</style>
